/*
 * Frequently Bought Together (sections/frequently-bought-together.liquid)
 * - Renders images, checklist, variant selects and a live total from the section's JSON
 * - Falls back to Shopify complementary recommendations when the metafield is empty
 * - Keeps "This item" in sync with the main product form's variant
 * - Adds all checked items in ONE /cart/add.js request, then opens the theme's cart
 *   drawer the same way Add to cart does (falls back to the cart page)
 */
if (!customElements.get('frequently-bought-together')) {
  customElements.define(
    'frequently-bought-together',
    class FrequentlyBoughtTogether extends HTMLElement {
      connectedCallback() {
        if (this.ready) return;
        this.ready = true;
        this.max = parseInt(this.dataset.max, 10) || 3;
        this.showCompare = this.dataset.showCompare !== 'false';
        this.imagesEl = this.querySelector('[data-fbt-images]');
        this.listEl = this.querySelector('[data-fbt-list]');
        this.totalEl = this.querySelector('[data-fbt-total]');
        this.button = this.querySelector('[data-fbt-add]');
        this.errorEl = this.querySelector('[data-fbt-error]');
        this.errorText = this.querySelector('[data-fbt-error-text]');
        this.section = this.closest('[data-fbt-section]');

        const current = this.readJSON('[data-fbt-current]');
        if (!current || !current.variants.length) return;

        this.loadExtras().then((extras) => {
          extras = extras
            .filter((p) => p && p.id !== current.id && p.variants && p.variants.length)
            .slice(0, this.max);
          if (!extras.length) return; // nothing to suggest: keep the section hidden

          this.items = [current].concat(extras).map((p, index) => {
            const variant =
              p.variants.find((v) => v.id === p.selected) || p.variants[0];
            return { product: p, variantId: variant.id, checked: true, isCurrent: index === 0, soldOut: false };
          });
          this.syncCurrentFromForm();
          this.render();
          this.button.addEventListener('click', () => this.addToCart());
          document.addEventListener('change', (event) => this.onPageChange(event));
          if (this.section) this.section.hidden = false;
        });
      }

      readJSON(selector, root) {
        const el = (root || this).querySelector(selector);
        if (!el) return null;
        try {
          return JSON.parse(el.textContent);
        } catch (e) {
          return null;
        }
      }

      loadExtras() {
        const extras = this.readJSON('[data-fbt-extras]') || [];
        const url = this.dataset.recommendationsUrl;
        if (extras.length || !url) return Promise.resolve(extras);
        return fetch(url)
          .then((res) => (res.ok ? res.text() : ''))
          .then((html) => {
            if (!html) return [];
            const doc = new DOMParser().parseFromString(html, 'text/html');
            return this.readJSON('[data-fbt-extras]', doc) || [];
          })
          .catch(() => []);
      }

      /* ---------- helpers ---------- */

      money(cents) {
        const format =
          (window.themeGlobalVariables && themeGlobalVariables.settings && themeGlobalVariables.settings.money_format) ||
          (window.cartStrings && cartStrings.money_format);
        // Self-contained: this script can run before theme.js defines Shopify.formatMoney
        const match = format && format.match(/\{\{\s*(\w+)\s*\}\}/);
        if (!match) return (cents / 100).toFixed(2);
        const withDelimiters = (precision, thousands, decimal) => {
          const [whole, fraction] = (cents / 100).toFixed(precision).split('.');
          return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (fraction ? decimal + fraction : '');
        };
        const styles = {
          amount: [2, ',', '.'],
          amount_no_decimals: [0, ',', '.'],
          amount_with_comma_separator: [2, '.', ','],
          amount_no_decimals_with_comma_separator: [0, '.', ','],
          amount_with_apostrophe_separator: [2, "'", '.'],
          amount_with_space_separator: [2, ' ', ','],
          amount_no_decimals_with_space_separator: [0, ' ', ','],
        };
        return format.replace(match[0], withDelimiters(...(styles[match[1]] || styles.amount)));
      }

      variantOf(item) {
        return item.product.variants.find((v) => v.id === item.variantId) || item.product.variants[0];
      }

      escape(str) {
        return String(str == null ? '' : str).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
      }

      /* ---------- main form sync ---------- */

      mainVariantInput() {
        const inputs = document.querySelectorAll('form[action*="/cart/add"] input[name="id"]');
        for (const input of inputs) {
          if (this.contains(input) || input.closest('sticky-add-cart, .product-item, [class*="quickview"]')) continue;
          if (input.value) return input;
        }
        return null;
      }

      syncCurrentFromForm() {
        const input = this.mainVariantInput();
        if (!input) return;
        this.setCurrentVariant(Number(input.value));
      }

      setCurrentVariant(id) {
        const item = this.items[0];
        const available = item.product.variants.some((v) => v.id === id);
        if (available) {
          item.variantId = id;
          if (item.soldOut) item.checked = true;
          item.soldOut = false;
        } else {
          // The main form picked a sold-out variant: this item can't be added
          item.soldOut = true;
          item.checked = false;
        }
      }

      onPageChange(event) {
        const target = event.target;
        if (!target || target.name !== 'id' || this.contains(target)) return;
        if (!target.closest || !target.closest('form[action*="/cart/add"]')) return;
        if (target.closest('sticky-add-cart, .product-item, [class*="quickview"]')) return;
        const id = Number(target.value);
        if (!id) return;
        this.setCurrentVariant(id);
        this.render();
      }

      /* ---------- render ---------- */

      render() {
        const focusedId = document.activeElement && this.contains(document.activeElement) ? document.activeElement.id : null;
        let images = '';
        let rows = '';
        let total = 0;
        let checkedCount = 0;
        const uid = this.dataset.uid || (this.dataset.uid = Math.random().toString(36).slice(2, 8));

        this.items.forEach((item, index) => {
          const p = item.product;
          const v = this.variantOf(item);
          const image = v.image || p.image;
          const on = item.checked && !item.soldOut;
          if (on) {
            total += v.price;
            checkedCount += 1;
          }
          const title = this.escape(p.title);

          if (index > 0) images += '<li class="fbt__plus" aria-hidden="true">+</li>';
          images +=
            '<li class="fbt__image' + (on ? '' : ' is-off') + '">' +
            '<a href="' + this.escape(p.url) + '" aria-label="' + title + '">' +
            (image
              ? '<img src="' + this.escape(image) + '" alt="' + title + '" width="180" height="180" loading="lazy">'
              : '<span class="fbt__image-empty">' + title + '</span>') +
            '</a></li>';

          const checkId = 'fbt-' + uid + '-check-' + index;
          const selectId = 'fbt-' + uid + '-variant-' + index;
          let select = '';
          if (!p.hasOnlyDefaultVariant) {
            select =
              '<label class="visually-hidden" for="' + selectId + '">Option for ' + title + '</label>' +
              '<select id="' + selectId + '" class="fbt__select" data-index="' + index + '"' + (item.soldOut ? ' disabled' : '') + '>' +
              p.variants
                .map((opt) => '<option value="' + opt.id + '"' + (opt.id === v.id ? ' selected' : '') + '>' + this.escape(opt.title) + '</option>')
                .join('') +
              (item.soldOut ? '<option selected>Sold out</option>' : '') +
              '</select>';
          }
          const onSale = this.showCompare && v.compare > v.price;
          rows +=
            '<li class="fbt__row' + (on ? '' : ' is-off') + '">' +
            '<input type="checkbox" id="' + checkId + '" class="fbt__check" data-index="' + index + '"' +
            (on ? ' checked' : '') + (item.soldOut ? ' disabled' : '') + '>' +
            '<label class="fbt__name" for="' + checkId + '">' +
            (item.isCurrent ? '<strong>This item: </strong>' : '') + title +
            (item.soldOut ? ' <span class="fbt__soldout">(selected option is sold out)</span>' : '') +
            '</label>' +
            select +
            '<span class="fbt__price">' +
            (onSale ? '<s class="fbt__compare">' + this.money(v.compare) + '</s> ' : '') +
            '<span class="' + (onSale ? 'fbt__sale' : '') + '">' + this.money(v.price) + '</span>' +
            '</span>' +
            '</li>';
        });

        this.imagesEl.innerHTML = images;
        this.listEl.innerHTML = rows;
        this.totalEl.textContent = this.money(total);
        this.button.disabled = checkedCount === 0 || this.adding;

        this.listEl.querySelectorAll('.fbt__check').forEach((box) =>
          box.addEventListener('change', () => {
            this.items[+box.dataset.index].checked = box.checked;
            this.hideError();
            this.render();
          })
        );
        this.listEl.querySelectorAll('.fbt__select').forEach((sel) =>
          sel.addEventListener('change', () => {
            this.items[+sel.dataset.index].variantId = Number(sel.value);
            this.hideError();
            this.render();
          })
        );
        if (focusedId) {
          const again = document.getElementById(focusedId);
          if (again) again.focus();
        }
      }

      /* ---------- add to cart ---------- */

      showError(message) {
        if (!this.errorEl) return;
        this.errorText.textContent = message || (window.cartStrings && cartStrings.error) || 'Could not add to cart. Please try again.';
        this.errorEl.hidden = false;
      }

      hideError() {
        if (this.errorEl) this.errorEl.hidden = true;
      }

      addToCart() {
        if (this.adding) return;
        const chosen = this.items.filter((item) => item.checked && !item.soldOut);
        if (!chosen.length) return;
        this.hideError();

        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const payload = { items: chosen.map((item) => ({ id: item.variantId, quantity: 1 })) };
        const canUseDrawer = cart && typeof cart.getSectionsToRender === 'function';
        if (canUseDrawer) {
          payload.sections = cart.getSectionsToRender().map((section) => section.id);
          payload.sections_url = window.location.pathname;
        }

        this.adding = true;
        this.button.disabled = true;
        this.button.classList.add('loading');
        this.button.setAttribute('aria-busy', 'true');

        const addUrl = (this.dataset.cartAddUrl || '/cart/add') + '.js';
        fetch(addUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload),
        })
          .then((res) => res.json().then((data) => ({ ok: res.ok, data })))
          .then(({ ok, data }) => {
            if (!ok || data.status) {
              // Nothing was added; keep the selection so the customer can adjust it
              this.showError(data.description || data.message);
              return;
            }
            if (window.publish && window.PUB_SUB_EVENTS) {
              try {
                publish(PUB_SUB_EVENTS.cartUpdate, { source: 'frequently-bought-together', cartData: data });
              } catch (e) {}
            }
            if (!canUseDrawer || !this.openDrawer(cart, data)) {
              window.location.href = this.dataset.cartUrl || '/cart';
            }
          })
          .catch(() => this.showError())
          .finally(() => {
            this.adding = false;
            this.button.classList.remove('loading');
            this.button.removeAttribute('aria-busy');
            this.button.disabled = !this.items.some((item) => item.checked && !item.soldOut);
          });
      }

      // Same steps as the theme's product-form after a successful add
      openDrawer(cart, data) {
        if (!data.sections) return false;
        try {
          cart.getSectionsToRender().forEach((section) => {
            const el = document.getElementById(section.id);
            const html = data.sections[section.id];
            if (!el || !html) return;
            const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('#minicart-form');
            if (fresh) el.innerHTML = fresh.innerHTML;
          });
          fetch('/cart.json')
            .then((res) => res.json())
            .then((c) => {
              if (c.item_count == null) return;
              document.querySelectorAll('.cart-count').forEach((el) => {
                el.innerHTML = el.classList.contains('cart-count-drawer')
                  ? '(' + c.item_count + ')'
                  : c.item_count > 100 ? '~' : c.item_count;
              });
              const headerTotal = document.querySelector('header-total-price');
              if (headerTotal && headerTotal.updateTotal) headerTotal.updateTotal(c);
              const freeShip = document.querySelector('free-ship-progress-bar');
              if (freeShip && freeShip.init) freeShip.init(c.items_subtotal_price);
            })
            .catch(() => {});
          if (typeof cart.cartAction === 'function') cart.cartAction();
          cart.classList.remove('is-empty');
          if (typeof cart.open !== 'function') return false;
          cart.open();
          return true;
        } catch (e) {
          return false;
        }
      }
    }
  );
}
